import React, { useEffect, useState } from "react";
import { gameActor } from "../hooks/useGameMachine.js";
import {
  listSlots,
  load,
  type SaveSlotSummary,
} from "../../engine/persistence/PersistenceManager.js";
import { bootAdventure } from "../../engine/adventure/bootAdventure.js";

/**
 * Save picker screen (P21).
 *
 * Groups the full save list by `(adventureId, partyId)` per the derived-party
 * rule in `specs/persistence.md`. Saves under each party header are sorted
 * newest-first. Tapping a save boots the adventure it belongs to, loads the
 * slot, and dispatches `LOAD_MAP` to transition the machine into `loading`.
 */
export function SavePicker() {
  const [slots, setSlots] = useState<readonly SaveSlotSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busySlot, setBusySlot] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSlots()
      .then((s) => {
        if (!cancelled) setSlots(s);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onPickSlot = async (slot: SaveSlotSummary) => {
    setBusySlot(slot.slotId);
    setError(null);
    try {
      await bootAdventure(slot.adventureId);
      const result = await load(slot.slotId);
      gameActor.send({
        type: "LOAD_MAP",
        mapId: result.file.state.pointer.mapId,
        targetCell: result.file.state.pointer.position,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setBusySlot(null);
    }
  };

  const groups = slots ? groupSlotsByParty(slots) : [];

  return (
    <div data-testid="save-picker" style={rootStyle}>
      <div style={headerRow}>
        <h1 style={{ margin: 0, fontSize: 20, letterSpacing: 3 }}>LOAD GAME</h1>
        <button
          type="button"
          onClick={() => {
            gameActor.send({ type: "SHOW_LAUNCH" });
          }}
          style={backBtnStyle}
        >
          ← Back
        </button>
      </div>

      {error && <div style={errStyle}>Error: {error}</div>}
      {slots === null && !error && <div style={mutedStyle}>Loading saves…</div>}
      {slots !== null && slots.length === 0 && (
        <div style={mutedStyle}>No saves found. Start a new game to create one.</div>
      )}

      {groups.map((g) => (
        <div key={`${g.adventureId}::${g.partyId}`} style={groupStyle}>
          <div style={groupHeaderStyle}>
            <span style={{ color: "#ffd700" }}>{g.partyName}</span>
            <span style={{ color: "#888", fontSize: 11, marginLeft: 8 }}>
              · {g.adventureId} · {g.roster}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {g.slots.map((s) => (
              <button
                key={s.slotId}
                type="button"
                data-testid={`save-slot-${s.slotId}`}
                disabled={busySlot !== null}
                onClick={() => void onPickSlot(s)}
                style={{
                  ...slotBtnStyle,
                  opacity: busySlot !== null && busySlot !== s.slotId ? 0.4 : 1,
                }}
              >
                <div style={{ fontSize: 13 }}>{s.displayName}</div>
                <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>
                  {s.meta.floorName} · {formatTimestamp(s.meta.timestamp)}
                  {busySlot === s.slotId ? " · loading…" : ""}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Grouping ────────────────────────────────────────────────────────────────
// Exported so tests can lock down the derived-party rule without touching the
// component.

export interface PartyGroup {
  readonly adventureId: string;
  readonly partyId: string;
  readonly partyName: string;
  readonly roster: string;
  readonly slots: readonly SaveSlotSummary[];
}

export function groupSlotsByParty(slots: readonly SaveSlotSummary[]): readonly PartyGroup[] {
  const bucket = new Map<string, { key: { adv: string; pid: string }; name: string; roster: string; slots: SaveSlotSummary[] }>();
  for (const s of slots) {
    const key = `${s.adventureId}::${s.meta.partyId}`;
    const existing = bucket.get(key);
    if (existing) {
      existing.slots.push(s);
      continue;
    }
    bucket.set(key, {
      key: { adv: s.adventureId, pid: s.meta.partyId },
      name: s.meta.partyName,
      roster: s.meta.party.map((p) => `${p.name} L${String(p.level)}`).join(", "),
      slots: [s],
    });
  }
  const groups: PartyGroup[] = [];
  for (const b of bucket.values()) {
    const sorted = [...b.slots].sort((a, b2) => b2.meta.timestamp.localeCompare(a.meta.timestamp));
    groups.push({
      adventureId: b.key.adv,
      partyId: b.key.pid,
      partyName: b.name,
      roster: b.roster,
      slots: sorted,
    });
  }
  // Adventure alpha, then newest party first within an adventure.
  groups.sort((a, b) => {
    if (a.adventureId !== b.adventureId) return a.adventureId.localeCompare(b.adventureId);
    const aTs = a.slots[0]?.meta.timestamp ?? "";
    const bTs = b.slots[0]?.meta.timestamp ?? "";
    return bTs.localeCompare(aTs);
  });
  return groups;
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ── Styles ──────────────────────────────────────────────────────────────────

const rootStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "#0a0a0a",
  color: "white",
  fontFamily: "monospace",
  padding: 32,
  overflowY: "auto",
  zIndex: 200,
  pointerEvents: "auto",
};

const headerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 24,
};

const backBtnStyle: React.CSSProperties = {
  background: "#1a1a1a",
  color: "white",
  border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 4,
  padding: "6px 12px",
  fontFamily: "monospace",
  fontSize: 12,
  cursor: "pointer",
};

const groupStyle: React.CSSProperties = {
  marginBottom: 24,
  padding: 16,
  background: "#111",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 6,
};

const groupHeaderStyle: React.CSSProperties = {
  fontSize: 14,
  marginBottom: 10,
};

const slotBtnStyle: React.CSSProperties = {
  textAlign: "left",
  background: "#1a1a1a",
  color: "white",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 4,
  padding: "10px 12px",
  fontFamily: "monospace",
  cursor: "pointer",
};

const errStyle: React.CSSProperties = {
  color: "#c66",
  fontSize: 13,
  marginBottom: 16,
};

const mutedStyle: React.CSSProperties = {
  color: "#888",
  fontSize: 13,
};
